import React from 'react';
import { Check, X, CircleDot, Minus, ArrowRight, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import type { LifecycleStep, LifecycleStepState } from '../../../utils/transactionLifecycleFlow';

type Props = {
  steps: LifecycleStep[];
  /** e.g. "Master wallet → game" */
  flowKindLabel?: string;
  className?: string;
};

function stateStyles(state: LifecycleStepState): {
  node: string;
  card: string;
  label: string;
} {
  switch (state) {
    case 'done':
      return {
        node: 'bg-emerald-600 text-white border-emerald-600',
        card: 'border-emerald-200 bg-emerald-50/60',
        label: 'text-emerald-800',
      };
    case 'current':
      return {
        node: 'bg-sky-600 text-white border-sky-600 ring-2 ring-sky-200',
        card: 'border-sky-300 bg-sky-50/80',
        label: 'text-sky-900',
      };
    case 'partial':
      return {
        node: 'bg-amber-500 text-white border-amber-500 ring-2 ring-amber-200',
        card: 'border-amber-300 bg-amber-50/80',
        label: 'text-amber-950',
      };
    case 'failed':
      return {
        node: 'bg-red-600 text-white border-red-600 ring-2 ring-red-200',
        card: 'border-red-300 bg-red-50',
        label: 'text-red-900',
      };
    case 'skipped':
      return {
        node: 'bg-slate-200 text-slate-500 border-slate-300',
        card: 'border-dashed border-slate-200 bg-slate-50/50 opacity-80',
        label: 'text-slate-500',
      };
    default:
      return {
        node: 'bg-white text-slate-400 border-slate-300',
        card: 'border-slate-200 bg-white',
        label: 'text-slate-500',
      };
  }
}

function StateIcon({ state }: { state: LifecycleStepState }) {
  if (state === 'done') return <Check className="h-3.5 w-3.5" strokeWidth={3} />;
  if (state === 'partial') return <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} />;
  if (state === 'failed') return <X className="h-3.5 w-3.5" strokeWidth={3} />;
  if (state === 'current') return <CircleDot className="h-3.5 w-3.5" />;
  if (state === 'skipped') return <Minus className="h-3.5 w-3.5" />;
  return <span className="h-1.5 w-1.5 rounded-full bg-current" />;
}

function statusText(state: LifecycleStepState): string {
  switch (state) {
    case 'done':
      return 'Success';
    case 'partial':
      return 'Partial';
    case 'current':
      return 'In progress';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    default:
      return 'Pending';
  }
}

const TransactionLifecycleFlow: React.FC<Props> = ({ steps, flowKindLabel, className }) => {
  const { timezone } = useTimezone();

  if (steps.length === 0) return null;

  return (
    <section className={cn('rounded-lg border bg-card', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold tracking-tight">Process flow</h3>
          {flowKindLabel ? (
            <p className="text-[11px] text-muted-foreground mt-0.5">{flowKindLabel}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-emerald-600" /> Done
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-sky-600" /> Current
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500" /> Partial
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-red-600" /> Failed
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-slate-300" /> Pending / skipped
          </span>
        </div>
      </div>

      {/* Compact horizontal flowchart */}
      <div className="overflow-x-auto px-3 py-3">
        <ol className="flex min-w-max items-stretch gap-0">
          {steps.map((step, index) => {
            const styles = stateStyles(step.state);
            const timeLabel = step.at ? formatInTimezone(step.at, timezone) : null;
            return (
              <li key={step.id} className="flex items-stretch">
                <div className={cn('w-[9.5rem] rounded-md border px-2.5 py-2', styles.card)}>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold',
                        styles.node,
                      )}
                      aria-hidden
                    >
                      <StateIcon state={step.state} />
                    </span>
                    <div className="min-w-0">
                      <div className={cn('truncate text-xs font-semibold leading-tight', styles.label)}>
                        {index + 1}. {step.label}
                      </div>
                      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {statusText(step.state)}
                      </div>
                    </div>
                  </div>
                  <p className="mt-1.5 text-[11px] leading-snug text-foreground/90">{step.action}</p>
                  <dl className="mt-1.5 space-y-0.5 text-[10px] leading-snug text-muted-foreground">
                    {timeLabel ? (
                      <div>
                        <dt className="inline font-semibold text-slate-500">Time: </dt>
                        <dd className="inline tabular-nums text-foreground">{timeLabel}</dd>
                      </div>
                    ) : (
                      <div>
                        <dt className="inline font-semibold text-slate-500">Time: </dt>
                        <dd className="inline">—</dd>
                      </div>
                    )}
                    {step.actor ? (
                      <div>
                        <dt className="inline font-semibold text-slate-500">By: </dt>
                        <dd className="inline text-foreground">{step.actor}</dd>
                      </div>
                    ) : null}
                    {step.reason ? (
                      <div
                        className={cn(
                          step.state === 'failed'
                            ? 'text-red-800'
                            : step.state === 'partial'
                              ? 'text-amber-900'
                              : '',
                        )}
                      >
                        <dt className="inline font-semibold">
                          {step.state === 'failed' ? 'Reason: ' : 'Note: '}
                        </dt>
                        <dd className="inline whitespace-pre-wrap break-words">{step.reason}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>
                {index < steps.length - 1 ? (
                  <div className="flex w-6 shrink-0 items-center justify-center" aria-hidden>
                    <ArrowRight className="h-3.5 w-3.5 text-[#001f3f]" strokeWidth={2.5} />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export default TransactionLifecycleFlow;
